import { FlightOffer } from '@/src/types/flightOffer'
import { getMobileDeviceType } from '@/src/utils/deviceType'
import { getCurrentLocale } from '@/src/utils/rtkQuery/getCurrentLocale'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

interface OfferTranslation {
    cardTitle: string
    cardDescription: string
    mobileImage: string
}

interface Offer {
    _id: string
    offerSlug: string
    offerSequence: number
    expiryDate: string
    offerStatus: string
    cardLink: string
    onlyCard: boolean
    code: string
    en: OfferTranslation
    ar: OfferTranslation
}

interface FilteredOffers {
    expired: Offer[]
    inactive: Offer[]
    totalFiltered: number
}

interface ApiResponse {
    message: string
    offers: Offer[]
    filtered: FilteredOffers
}

// App Interface Types

interface OffersResponse {
    offers: FlightOffer[]
    totalCount?: number
}

interface OffersQueryParams {
    domain: string
    homepageBanner?: boolean
}

const baseQuery = fetchBaseQuery({
    baseUrl: '',
    prepareHeaders: headers => {
        const defaultHeaders = setCommonHeaders(headers)
        defaultHeaders.set('api-version', '3.0')
        defaultHeaders.set('x-traffic-source', 'cleartrip')

        if (typeof window !== 'undefined') {
            const referrer = window.location.href
            if (referrer) {
                defaultHeaders.set('referer', referrer)
            }
        }

        return defaultHeaders
    }
})

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 2 })

export const offersApi = createApi({
    reducerPath: 'offersApi',
    baseQuery: baseQueryWithRetry,
    tagTypes: ['FlightOffers', 'FlightOffer'],
    endpoints: builder => ({
        getFlightOffers: builder.query<OffersResponse, OffersQueryParams>({
            query: ({ domain, homepageBanner }) => {
                // Determine if it's a mobile app (Android or iOS)
                const mobileDeviceType = getMobileDeviceType()
                const isMobileApp = mobileDeviceType === 'AndroidApp' || mobileDeviceType === 'iPhoneApp'

                // Build params object
                const params: Record<string, any> = {
                    domain
                }

                // Add device-specific parameter
                if (isMobileApp) {
                    params.mobileApp = true
                } else {
                    params.webApp = true
                }

                // Add homepageBanner parameter if provided
                if (homepageBanner) {
                    params.homepageBanner = true
                }

                return {
                    url: '/node/flight/offers',
                    method: 'GET',
                    params
                }
            },
            transformResponse: (response: ApiResponse) => {
                const locale = getCurrentLocale()
                const isArabic = locale === 'ar'
                const allOffers: FlightOffer[] = []

                if (response.offers && Array.isArray(response.offers)) {
                    response.offers.forEach((offer, index) => {
                        const translation = isArabic ? offer.ar : offer.en
                        const imageUrl = translation.mobileImage || ''

                        allOffers.push({
                            offerId: offer._id,
                            title: translation.cardTitle,
                            description: translation.cardDescription,
                            imageUrl: imageUrl,
                            validTill: offer.expiryDate,
                            link: offer.cardLink,
                            priority: offer.offerSequence,
                            isActive: offer.offerStatus === 'active',
                            slug: offer.offerSlug,
                            couponCode: offer.code
                        })
                    })
                }

                return {
                    offers: allOffers,
                    totalCount: allOffers.length
                }
            },
            providesTags: ['FlightOffers']
        }),

        getFlightOfferById: builder.query<FlightOffer, { offerId: string; domain: string }>({
            query: ({ offerId, domain }) => {
                // Determine if it's a mobile app (Android or iOS)
                const mobileDeviceType = getMobileDeviceType()
                const isMobileApp = mobileDeviceType === 'AndroidApp' || mobileDeviceType === 'iPhoneApp'

                // Build params object
                const params: Record<string, any> = {
                    domain
                }

                // Add device-specific parameter
                if (isMobileApp) {
                    params.mobileApp = true
                } else {
                    params.webApp = true
                }

                return {
                    url: `/node/flight/offers/${offerId}`,
                    method: 'GET',
                    params
                }
            },
            transformResponse: (response: any, _meta, arg) => {
                const locale = getCurrentLocale()
                const isArabic = locale === 'ar'

                if (response.result && response.result.offer) {
                    const offer = response.result.offer
                    const translation = isArabic ? offer.ar : offer.en

                    // Get locale-specific images
                    const desktopImageUrl = translation.desktopImage || translation.mobileImage || ''
                    const mobileImageUrl = translation.mobileImage || desktopImageUrl || ''

                    return {
                        offerId: offer._id || arg.offerId,
                        title: translation.cardTitle,
                        heading: translation.heading,
                        description: translation.cardDescription,
                        paragraph: translation.paragraph,
                        imageUrl: mobileImageUrl, // Default to mobile for backward compatibility
                        desktopImageUrl: desktopImageUrl,
                        mobileImageUrl: mobileImageUrl,
                        validTill: offer.expiryDate,
                        travelPeriod: translation.travelPeriod,
                        bookingPeriod: translation.bookingPeriod,
                        termsAndConditions: translation.termsAndCondition || [],
                        tables: translation.tables || [],
                        link: offer.cardLink,
                        priority: offer.offerSequence,
                        isActive: offer.offerStatus === 'active',
                        slug: offer.offerSlug,
                        couponCode: offer.code
                    }
                }

                return {
                    offerId: arg.offerId,
                    title: 'Offer Details',
                    description: 'Offer information not available',
                    imageUrl: '',
                    desktopImageUrl: '',
                    mobileImageUrl: '',
                    isActive: false
                }
            },
            providesTags: (_result, _error, arg) => [{ type: 'FlightOffer', id: arg.offerId }]
        })
    })
})

// Export hooks for usage in components
export const { useGetFlightOffersQuery, useGetFlightOfferByIdQuery } = offersApi

export default offersApi
